// LudoModals — Question, Hazard, Boss, Tip, Star, Shortcut, and Win modals.
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Star, Zap, Crown, Lightbulb, ArrowBigRight, Trophy, X, CheckCircle2, XCircle, Award, Sparkles } from 'lucide-react';

function classNames(...xs) { return xs.filter(Boolean).join(' '); }

export function QuestionModal({ open, kind, question, onAnswer, onClose, timerSeconds = 12 }) {
    const [picked, setPicked] = useState(null);
    const [seconds, setSeconds] = useState(timerSeconds);
    useEffect(() => {
        if (!open) { setPicked(null); setSeconds(timerSeconds); return; }
        setPicked(null); setSeconds(timerSeconds);
        const t = setInterval(() => {
            setSeconds((s) => {
                if (s <= 1) {
                    clearInterval(t);
                    if (!picked) onAnswer(null);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open || !question) return null;

    const meta = MODAL_META[kind] || MODAL_META.question;
    const Icon = meta.icon;
    const handlePick = (opt) => {
        if (picked) return;
        const correct = opt === question.correct;
        setPicked({ opt, correct });
        setTimeout(() => onAnswer(correct), 1400);
    };

    const pct = (seconds / timerSeconds) * 100;

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                data-testid={`ludo-modal-${kind}`}
            >
                <motion.div initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                    className={`bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl ring-1 ring-white/10`}>
                    <div className={`${meta.headerBg} text-white p-4 sm:p-5 flex items-center gap-3`}>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"><Icon className="h-5 w-5 sm:h-6 sm:w-6" /></div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-90">{meta.label}</div>
                            <div className="font-display font-extrabold text-lg sm:text-xl">{meta.title}</div>
                        </div>
                        <div className="ml-auto text-right">
                            <div className="text-[10px] uppercase tracking-wide opacity-80">Time</div>
                            <div className="font-display font-extrabold text-xl" data-testid="ludo-modal-timer">{seconds}s</div>
                        </div>
                    </div>
                    <div className="h-1 bg-slate-100"><motion.div className="h-full bg-amber-400" animate={{ width: `${pct}%` }} transition={{ ease: 'linear', duration: 0.5 }} /></div>

                    <div className="p-5 sm:p-6">
                        <h3 className="font-display font-bold text-base sm:text-lg text-slate-900">{question.question}</h3>
                        <div className="mt-4 grid gap-2">
                            {question.options.map((opt, i) => {
                                const isPicked = picked?.opt === opt;
                                const isCorrect = picked && opt === question.correct;
                                const cls = !picked
                                    ? 'border-slate-200 hover:border-slate-900 hover:bg-slate-50 bg-white'
                                    : isCorrect ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                                        : isPicked ? 'border-rose-500 bg-rose-50 text-rose-800'
                                            : 'border-slate-200 bg-white opacity-50';
                                return (
                                    <button key={i} onClick={() => handlePick(opt)} disabled={!!picked}
                                        className={`text-left px-4 py-3 rounded-2xl border-2 text-sm sm:text-base font-semibold transition flex items-center gap-2 ${cls}`}
                                        data-testid={`ludo-q-opt-${i}`}>
                                        <span className="inline-flex h-6 w-6 rounded-lg bg-slate-100 items-center justify-center text-xs font-extrabold text-slate-700">{['A', 'B', 'C', 'D'][i]}</span>
                                        <span className="flex-1">{opt}</span>
                                        {picked && isCorrect && <CheckCircle2 className="h-5 w-5" />}
                                        {picked && isPicked && !isCorrect && <XCircle className="h-5 w-5" />}
                                    </button>
                                );
                            })}
                        </div>

                        {picked && question.explanation && (
                            <div className="mt-4 p-3 rounded-2xl bg-slate-50 border border-slate-200">
                                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-1">Learning insight</div>
                                <div className="text-sm text-slate-700">{question.explanation}</div>
                            </div>
                        )}

                        <div className="mt-4 text-[11px] text-slate-500 flex items-center justify-between">
                            <span>{meta.reward}</span>
                            <span className="text-slate-400">{meta.penalty}</span>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

const MODAL_META = {
    question: { icon: HelpCircle, label: 'Question Square', title: 'Quick Question', headerBg: 'bg-gradient-to-r from-sky-600 to-blue-700', reward: 'Correct: +20 pts · roll again', penalty: 'Wrong: −4 cells' },
    hazard:   { icon: Zap,        label: 'Safety Hazard',  title: 'Safety Scenario',   headerBg: 'bg-gradient-to-r from-rose-600 to-red-700', reward: 'Correct: +30 pts · roll again', penalty: 'Wrong: −6 cells' },
    shortcut: { icon: ArrowBigRight, label: 'Shortcut Gate', title: 'Unlock the Shortcut', headerBg: 'bg-gradient-to-r from-fuchsia-600 to-pink-700', reward: 'Correct: +10 cells leap · +25 pts', penalty: 'Wrong: stay where you are' },
};

export function StarModal({ open, item, onClose }) {
    if (!open || !item) return null;
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ludo-modal-star">
                <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 p-5 flex items-center gap-3">
                        <Star className="h-7 w-7" />
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em]">Knowledge Star</div>
                            <div className="font-display font-extrabold text-xl">{item.title}</div>
                        </div>
                    </div>
                    <div className="p-5">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-amber-600 mb-2 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />Did you know?</div>
                        <p className="text-sm text-slate-700 leading-relaxed">{item.body}</p>
                        <div className="mt-4 text-[11px] text-emerald-700 font-semibold">+10 pts collected</div>
                        <button onClick={onClose} className="mt-4 w-full px-4 py-3 rounded-2xl bg-slate-900 text-white font-semibold" data-testid="ludo-star-continue">Continue →</button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export function TipModal({ open, tip, onClose }) {
    if (!open || !tip) return null;
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ludo-modal-tip">
                <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                    <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 p-4 flex items-center gap-3">
                        <Lightbulb className="h-6 w-6" />
                        <div className="font-display font-extrabold text-lg">{tip.title}</div>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-slate-700 leading-relaxed">{tip.body}</p>
                        <div className="mt-4 text-[11px] text-emerald-700 font-semibold">+5 pts learned</div>
                        <button onClick={onClose} className="mt-4 w-full px-4 py-3 rounded-2xl bg-slate-900 text-white font-semibold" data-testid="ludo-tip-continue">Got it →</button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export function BossModal({ open, questions, onComplete }) {
    const [idx, setIdx] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [picked, setPicked] = useState(null);

    useEffect(() => { if (open) { setIdx(0); setCorrectCount(0); setPicked(null); } }, [open]);

    if (!open || !questions || questions.length === 0) return null;
    const q = questions[idx];

    const pick = (opt) => {
        if (picked) return;
        const ok = opt === q.correct;
        setPicked({ opt, correct: ok });
        setTimeout(() => {
            const newCount = correctCount + (ok ? 1 : 0);
            if (idx + 1 >= questions.length) {
                onComplete(newCount, questions.length);
            } else {
                setIdx(idx + 1); setCorrectCount(newCount); setPicked(null);
            }
        }, 1100);
    };

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ludo-modal-boss">
                <motion.div initial={{ scale: 0.8, rotate: -2 }} animate={{ scale: 1, rotate: 0 }} className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl ring-2 ring-violet-400">
                    <div className="bg-gradient-to-r from-violet-700 to-fuchsia-700 text-white p-4 sm:p-5 flex items-center gap-3">
                        <Crown className="h-6 w-6 sm:h-7 sm:w-7 text-amber-300" />
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em]">Boss Round</div>
                            <div className="font-display font-extrabold text-xl sm:text-2xl">Master Challenge</div>
                        </div>
                        <div className="ml-auto text-right">
                            <div className="text-[10px] uppercase tracking-wide opacity-80">Question</div>
                            <div className="font-display font-extrabold text-xl">{idx + 1} / {questions.length}</div>
                        </div>
                    </div>
                    <div className="h-1.5 bg-violet-900"><motion.div className="h-full bg-amber-400" animate={{ width: `${((idx + (picked ? 1 : 0)) / questions.length) * 100}%` }} /></div>

                    <div className="p-5 sm:p-6">
                        <h3 className="font-display font-bold text-base sm:text-lg text-slate-900">{q.question}</h3>
                        <div className="mt-4 grid gap-2">
                            {q.options.map((opt, i) => {
                                const isPicked = picked?.opt === opt;
                                const isCorrect = picked && opt === q.correct;
                                const cls = !picked
                                    ? 'border-slate-200 hover:border-violet-700 hover:bg-violet-50 bg-white'
                                    : isCorrect ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                                        : isPicked ? 'border-rose-500 bg-rose-50 text-rose-800'
                                            : 'border-slate-200 bg-white opacity-50';
                                return (
                                    <button key={i} onClick={() => pick(opt)} disabled={!!picked}
                                        className={`text-left px-4 py-3 rounded-2xl border-2 text-sm sm:text-base font-semibold transition flex items-center gap-2 ${cls}`}
                                        data-testid={`ludo-boss-opt-${i}`}>
                                        <span className="inline-flex h-6 w-6 rounded-lg bg-violet-100 items-center justify-center text-xs font-extrabold text-violet-700">{['A', 'B', 'C', 'D'][i]}</span>
                                        <span className="flex-1">{opt}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-2"><Crown className="h-3 w-3 text-violet-600" />3/3 = +100 pts + capture shield · 2/3 = +50 · 0–1 = −10 cells</div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export function WinModal({ open, won, score, capturedCount, learnedCount, badgeUnlocked, onClose, onPlayAgain }) {
    if (!open) return null;
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ludo-modal-win">
                <motion.div initial={{ scale: 0.5, rotate: -4 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 220, damping: 16 }}
                    className={classNames('rounded-3xl w-full max-w-md overflow-hidden shadow-2xl ring-2',
                        won ? 'bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-500 ring-amber-300' : 'bg-gradient-to-br from-slate-700 to-slate-900 ring-slate-500')}
                >
                    <div className="p-6 text-center">
                        {won ? (
                            <>
                                <motion.div animate={{ y: [-6, 0, -6] }} transition={{ repeat: Infinity, duration: 1.4 }}>
                                    <Trophy className="h-16 w-16 mx-auto text-amber-900 drop-shadow" />
                                </motion.div>
                                <div className="font-display font-extrabold text-3xl text-slate-900 mt-3">VICTORY!</div>
                                <div className="text-sm text-slate-800 mt-1">All 4 tokens home. Now THAT's mastery.</div>
                            </>
                        ) : (
                            <>
                                <Award className="h-14 w-14 mx-auto text-slate-300" />
                                <div className="font-display font-extrabold text-3xl text-white mt-3">Bot Wins This Round</div>
                                <div className="text-sm text-slate-300 mt-1">Good game! Try again — and pick a different topic next time.</div>
                            </>
                        )}
                    </div>
                    <div className={classNames('px-6 pb-6 grid grid-cols-3 gap-2 text-center', won ? 'text-slate-900' : 'text-white')}>
                        <Stat label="Score" value={score} won={won} />
                        <Stat label="Captures" value={capturedCount} won={won} />
                        <Stat label="Learned" value={learnedCount} won={won} />
                    </div>
                    {badgeUnlocked && (
                        <div className="mx-6 mb-4 p-4 rounded-2xl bg-white/95 border-2 border-amber-300 flex items-center gap-3" data-testid="ludo-badge-unlock">
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center"><Crown className="h-6 w-6 text-amber-700" /></div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide font-bold text-amber-700">Badge Unlocked</div>
                                <div className="font-display font-extrabold text-slate-900">Ludo Master</div>
                                <div className="text-[11px] text-slate-600">Won 3+ Ludo learning games</div>
                            </div>
                        </div>
                    )}
                    <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2">
                        <button onClick={onPlayAgain} className={classNames('flex-1 px-4 py-3 rounded-2xl font-semibold',
                            won ? 'bg-slate-900 text-amber-400 hover:bg-slate-800' : 'bg-amber-400 text-slate-900 hover:bg-amber-300')}
                            data-testid="ludo-play-again-btn">Play Again</button>
                        <button onClick={onClose} className={classNames('flex-1 px-4 py-3 rounded-2xl font-semibold',
                            won ? 'bg-white/80 text-slate-900 hover:bg-white' : 'bg-slate-700 text-white hover:bg-slate-600')}
                            data-testid="ludo-win-close-btn">Back to Arcade</button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function Stat({ label, value, won }) {
    return (
        <div className={classNames('rounded-2xl p-3', won ? 'bg-white/40' : 'bg-white/10')}>
            <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
            <div className="font-display font-extrabold text-xl">{value}</div>
        </div>
    );
}

export function TopicPicker({ open, presets, onPick, onClose, loading, errorText, numBots, onChangeNumBots }) {
    const [custom, setCustom] = useState('');
    if (!open) return null;
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ludo-modal-topic">
                <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }}
                    className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl">
                    <div className="bg-gradient-to-r from-brand-black to-slate-900 text-brand-yellow p-5 flex items-center gap-3">
                        <Sparkles className="h-7 w-7" />
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">Propulse Ludo Learning</div>
                            <div className="font-display font-extrabold text-2xl">Set up your match</div>
                        </div>
                    </div>
                    <div className="p-5 sm:p-6">
                        {/* Bot count selector */}
                        <div className="mb-4">
                            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">AI opponents</label>
                            <div className="mt-2 grid grid-cols-3 gap-2">
                                {[1, 2, 3].map((n) => (
                                    <button key={n} disabled={loading}
                                        onClick={() => onChangeNumBots(n)}
                                        className={`relative px-3 py-3 rounded-2xl border-2 font-display font-extrabold text-base transition ${numBots === n ? 'border-brand-black bg-brand-black text-brand-yellow' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
                                        data-testid={`ludo-bots-${n}`}>
                                        {n} bot{n > 1 ? 's' : ''}
                                        <div className="text-[10px] font-semibold opacity-80 mt-0.5">{n === 1 ? 'Quick match' : n === 2 ? 'Triangle' : 'Full classic'}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="border-t border-slate-100 pt-4">
                            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Learning topic</label>
                            <p className="text-[11px] text-slate-500 mt-1 mb-2">Our AI tutor generates 20 fresh questions on this topic. Wrong answers slow you down — right answers earn points + a free lesson.</p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                            {presets.map((p) => (
                                <button key={p} disabled={loading} onClick={() => onPick(p)}
                                    className="text-left px-4 py-3 rounded-2xl border-2 border-slate-200 hover:border-brand-black hover:bg-slate-50 text-sm font-semibold text-slate-800 disabled:opacity-50"
                                    data-testid={`ludo-topic-${p.toLowerCase().slice(0, 16).replace(/[^a-z]+/g, '-')}`}>
                                    {p}
                                </button>
                            ))}
                        </div>
                        <div className="mt-4">
                            <label className="text-xs font-semibold text-slate-700">Or type your own topic</label>
                            <div className="mt-1 flex gap-2">
                                <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. XLPE cable troubleshooting"
                                    className="flex-1 px-3 py-2.5 rounded-2xl border-2 border-slate-200 text-sm" data-testid="ludo-topic-custom-input" />
                                <button disabled={loading || !custom.trim()} onClick={() => onPick(custom.trim())}
                                    className="px-4 py-2.5 rounded-2xl bg-brand-black text-brand-yellow font-semibold disabled:opacity-50" data-testid="ludo-topic-custom-go">Go</button>
                            </div>
                        </div>
                        {loading && (
                            <div className="mt-5 p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.4, ease: 'linear' }} className="inline-block">
                                    <Sparkles className="h-6 w-6 text-amber-500" />
                                </motion.div>
                                <div className="font-display font-bold text-slate-900 mt-2">Your AI tutor is preparing 20 questions…</div>
                                <div className="text-xs text-slate-500 mt-1">Usually 15–30 seconds. We'll fall back to a curated bank if it takes too long.</div>
                            </div>
                        )}
                        {errorText && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl p-3" data-testid="ludo-topic-error">{errorText}</div>}
                        <button onClick={onClose} className="mt-4 w-full text-xs text-slate-500 hover:text-slate-900" data-testid="ludo-topic-skip">Use curated cables & wires bank instead</button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
